import { Platform } from 'react-native'

import { FormattedMessage } from 'react-intl'

import Box from 'app/components/base/Box'
import ScrolledContainer from 'app/components/base/Scroll/ScrolledContainer'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import ProductCard from 'app/components/cards/ProductCard'
import { LISTING_STATUS } from 'app/utils/constants/status.constants'
import { getFirstUrlAttachment } from 'app/utils/helpers/functions.helpers'

import NavigationSlideButton from '../components/NavigationSlideButton'

import { useYouRecentlyViewed } from './hooks/useYouRecentlyViewed'

interface YouRecentlyViewedProps {
  slugs: string[]
  isMobile?: boolean
  loading?: boolean
  isLargeSize?: boolean
}

const YouRecentlyViewed: RFC<YouRecentlyViewedProps> = ({
  slugs,
  isMobile,
  loading,
  isLargeSize
}) => {
  const {
    data,
    endIndex,
    isLoading,
    setStartIndex,
    startIndex,
    maxCards,
    currency
  } = useYouRecentlyViewed({
    slugs,
    isLargeSize,
    isMobile
  })

  if (!data || !data.length) return null

  return (
    <Stack sx={{ width: '100%', maxWidth: 1154 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', px: 3 }}>
        <Text variant="h2">
          <FormattedMessage defaultMessage="You recently viewed" />
        </Text>

        {data.length > maxCards && (
          <Stack spacing={1} direction="row">
            <NavigationSlideButton
              rotate
              disabled={startIndex === 0}
              onClick={() => {
                setStartIndex((prev) => prev - 1)
              }}
            />

            <NavigationSlideButton
              disabled={endIndex === data.length}
              onClick={() => {
                setStartIndex((prev) => prev + 1)
              }}
            />
          </Stack>
        )}
      </Stack>

      <ScrolledContainer disable={!isMobile} horizontal>
        <Stack
          direction="row"
          spacing={{ md: 3.75, xs: 1 }}
          sx={{
            pt: 3,
            pb: 4,
            px: { md: 3, xs: 2 },
            ...(isLargeSize && !isMobile && { justifyContent: 'center' }),
            ...Platform.select({
              web: {
                ...(isMobile && { width: 'fit-content' })
              }
            })
          }}
        >
          {data.slice(startIndex, startIndex + maxCards).map((listing) => {
            const location =
              listing?.shipping?.collection_address?.city ||
              listing?.shipping?.dispatch_address?.city ||
              ''
            const link = `/listing/${listing?.slug}`
            const image = getFirstUrlAttachment(listing?.attachments) || ''
            const isSold = listing?.status === LISTING_STATUS.sold_out

            return (
              <Box
                key={listing?.id}
                sx={{
                  minWidth: { xs: 165, md: 198 },
                  width: { xs: 165, md: 198 }
                }}
              >
                <ProductCard
                  location={location}
                  loading={isLoading || loading}
                  image={image}
                  title={listing?.title}
                  subTitle={listing.subtitle}
                  price={listing?.converted_actual_price}
                  currency={currency}
                  auctionEndDate={listing?.auction?.end_date}
                  auctionReserveStatus={listing?.auction?.reserve_status}
                  link={link}
                  id={listing?.id}
                  isLiked={listing?.liked}
                  listingUserId={listing?.user?.id}
                  isSold={isSold}
                />
              </Box>
            )
          })}
        </Stack>
      </ScrolledContainer>
    </Stack>
  )
}

export default YouRecentlyViewed
